import type { MiSancion } from '../datos/index.js'
import { fechaLarga } from '../formato.js'

/**
 * Sus tarjetas en un torneo.
 *
 * Es la respuesta a la pregunta que antes no tenía dónde contestarse: el
 * expulsado abría la web y veía su torneo cortado, su fila «retirado» y nada
 * más. Acá está qué le sacaron, en qué ronda y lo que el árbitro escribió.
 *
 * Solo la ve él: el motivo llega por `/yo` y nunca por la vitrina. A los demás
 * les queda la marca de expulsión en la tabla, que es lo mismo que se ve en la
 * planilla del salón.
 */

const NOMBRE: Record<MiSancion['tipo'], string> = {
  verbal: 'Amonestación verbal',
  amarilla: 'Tarjeta amarilla',
  roja: 'Tarjeta roja',
  negra: 'Tarjeta negra',
}

/** El color de la pastilla: las que sacan del torneo van en rojo. */
const CLASE: Record<MiSancion['tipo'], string> = {
  verbal: 'neutra',
  amarilla: 'alerta',
  roja: 'error',
  negra: 'error',
}

/** La que lo sacó del torneo, si alguna lo sacó. La primera: después ya no jugó. */
export function expulsionDe(sanciones: readonly MiSancion[]): MiSancion | null {
  return sanciones.find((s) => s.tipo === 'roja' || s.tipo === 'negra') ?? null
}

function cuando(s: MiSancion): string {
  return s.rondaNumero === null ? 'Antes de empezar' : `Ronda ${s.rondaNumero}`
}

/**
 * El aviso de arriba, para el expulsado. Va aparte de la lista porque se usa
 * también en «Mi mesa»: es lo primero que tiene que leer el que entra a buscar
 * una mesa que ya no va a tener.
 */
export function AvisoExpulsion({ sanciones }: { sanciones: readonly MiSancion[] }) {
  const expulsion = expulsionDe(sanciones)
  if (!expulsion) return null

  const donde =
    expulsion.rondaNumero === null ? 'antes de empezar' : `en la ronda ${expulsion.rondaNumero}`

  return (
    <div className="aviso error">
      <strong>Quedaste fuera de este torneo</strong>
      <span>
        La organización te sacó con una {expulsion.tipo === 'negra' ? 'tarjeta negra' : 'tarjeta roja'}{' '}
        {donde}. Las partidas que jugaste antes siguen contando en la tabla.
        {expulsion.venceAt &&
          ` La suspensión sigue vigente hasta el ${fechaLarga(expulsion.venceAt)}, también para otros torneos.`}
      </span>
    </div>
  )
}

export function MisSanciones({ sanciones }: { sanciones: readonly MiSancion[] }) {
  if (sanciones.length === 0) return null

  return (
    <ul className="mis-sanciones">
      {sanciones.map((s) => (
        <li key={s.aplicadaAt + s.tipo}>
          <div className="mis-sanciones-cabecera">
            <span className={`pastilla ${CLASE[s.tipo]}`}>{NOMBRE[s.tipo]}</span>
            <span className="tenue">{cuando(s)}</span>
          </div>
          <p>{s.motivo}</p>
          {s.venceAt && <p className="tenue">Vigente hasta el {fechaLarga(s.venceAt)}.</p>}
        </li>
      ))}
    </ul>
  )
}
